/**
 * WiseFieldRenderer.js
 *
 * Generic renderer that maps Wise's account-requirements field schema to MUI inputs.
 * Supports all field types Wise returns:
 *   - text   → TextField (with validationRegexp, displayFormat, minLength, maxLength)
 *   - select → Select + MenuItems (from valuesAllowed)
 *   - radio  → RadioGroup + FormControlLabels (from valuesAllowed)
 *   - date   → TextField type="date"
 *
 * Each field may have refreshRequirementsOnChange=true, in which case calling
 * onRefreshNeeded triggers a re-fetch of the requirements with the current form state.
 *
 * Props:
 *   field            {object}   Wise field group item (key, type, name, required, ...)
 *   value            {string}   Current form value
 *   onChange         {function} (value: string) => void
 *   onRefreshNeeded  {function} Called when refreshRequirementsOnChange=true and value changes
 *   error            {string}   Validation error message (if any)
 */
import React from 'react';
import {
  TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText,
  RadioGroup, FormControlLabel, Radio, Typography,
} from '@mui/material';

export default function WiseFieldRenderer({ field, value, onChange, onRefreshNeeded, error }) {
  const {
    key,
    name,
    type,
    required,
    displayFormat,
    example,
    minLength,
    maxLength,
    validationRegexp,
    valuesAllowed,
    refreshRequirementsOnChange,
  } = field;

  const handleChange = (newValue) => {
    onChange(newValue);
    if (refreshRequirementsOnChange && onRefreshNeeded) {
      onRefreshNeeded(key, newValue);
    }
  };

  const helperText = error || (example ? `e.g. ${example}` : displayFormat || '');

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  if (type === 'select' && valuesAllowed?.length > 0) {
    return (
      <FormControl fullWidth size="small" error={!!error}>
        <InputLabel>{name}</InputLabel>
        <Select
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          label={name}
          required={required}
          sx={{ borderRadius: 2 }}
        >
          {(valuesAllowed || []).map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.name}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  if (type === 'radio' && valuesAllowed?.length > 0) {
    return (
      <FormControl component="fieldset" error={!!error}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {name}{required && ' *'}
        </Typography>
        <RadioGroup
          row
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
        >
          {(valuesAllowed || []).map((opt) => (
            <FormControlLabel
              key={opt.key}
              value={opt.key}
              control={<Radio size="small" />}
              label={opt.name}
            />
          ))}
        </RadioGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  if (type === 'date') {
    return (
      <TextField
        label={name}
        type="date"
        value={value || ''}
        onChange={(e) => handleChange(e.target.value)}
        fullWidth
        size="small"
        required={required}
        error={!!error}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
        sx={inputSx}
      />
    );
  }

  // Default: text field
  return (
    <TextField
      label={name}
      value={value || ''}
      onChange={(e) => {
        let v = e.target.value;
        if (maxLength) v = v.slice(0, maxLength);
        handleChange(v);
      }}
      fullWidth
      size="small"
      required={required}
      error={!!error}
      helperText={helperText}
      placeholder={displayFormat || example || ''}
      inputProps={{ minLength, maxLength, pattern: validationRegexp || undefined }}
      sx={inputSx}
    />
  );
}
